package org.teddapp.project.ui.finance

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Finance_category
import org.teddapp.project.ui.DecisionOption
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddConsigne
import org.teddapp.project.ui.TeddDecision
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType

/** Verdicts du filtre cartésien, stockés sur la transaction. */
object Verdict {
    const val NEED = "need"
    const val MASTERED = "mastered"
    const val IMPULSE = "impulse"
}

/** Une dépense en attente de verdict : le filtre s'affiche avant d'écrire la transaction. */
internal data class PendingExpense(
    val accountId: String,
    val date: String,
    val amount: Long,
    val description: String?,
    val catId: String?,
    val currency: String,
)

/**
 * Seuil au-delà duquel une dépense passe par le filtre. Une dépense « majeure » vaut au moins
 * un quart du solde du compte, avec un plancher pour les petits comptes.
 */
internal fun needsCartesianFilter(amount: Long, balance: Long): Boolean {
    val threshold = maxOf(balance / 4, 25_000L)
    return amount >= threshold
}

/**
 * Filtre cartésien : « s'efforcer plutôt à se vaincre soi-même que la fortune, et à changer
 * ses désirs que l'ordre du monde. » Deux réponses équivalentes. Maîtriser le désir annule
 * la dépense et compte comme décision réussie ; le besoin réel l'enregistre normalement.
 */
@Composable
internal fun CartesianFilterScreen(
    pending: PendingExpense,
    categories: List<Finance_category>,
    onNeed: () -> Unit,
    onMastered: () -> Unit,
    onDismiss: () -> Unit,
) {
    val category = categories.firstOrNull { it.id == pending.catId }?.name
    Dialog(onDismissRequest = onDismiss, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        TeddConsigne(
            eyebrow = buildString {
                append("Nouvelle dépense · ")
                append(fmtMoney(pending.amount, pending.currency, compact = false))
                if (category != null) append(" · $category")
            },
            text = "S'efforcer plutôt à se vaincre soi-même que la fortune, et à changer ses désirs que l'ordre du monde.",
            modifier = Modifier.fillMaxSize(),
        ) {
            TeddDecision(
                first = DecisionOption("Besoin réel", "nécessaire à ma vie ou à mon apprentissage"),
                second = DecisionOption("Désir que je maîtrise", "j'annule la dépense · décision réussie"),
                onFirst = onNeed,
                onSecond = onMastered,
                stacked = true,
                modifier = Modifier.fillMaxWidth(),
            )
            Text(
                text = "Ce filtre n'apparaît que pour les dépenses majeures. Rien n'est jugé : le verdict sert au taux de décision du mois.",
                color = TeddTokens.InkMuted,
                style = TeddType.Body,
                textAlign = TextAlign.Center,
                modifier = Modifier.padding(top = 6.dp),
            )
            TeddButton(
                text = "Enregistrer sans répondre",
                onClick = onDismiss,
                kind = TeddButtonKind.Ghost,
                size = TeddButtonSize.Md,
            )
        }
    }
}

/** Taux de décisions rationnelles du mois : besoins réels et désirs maîtrisés sur l'ensemble des verdicts. */
internal fun rationalRate(verdicts: List<String?>): Int? {
    val known = verdicts.filterNotNull().filter { it.isNotBlank() }
    if (known.isEmpty()) return null
    val rational = known.count { it == Verdict.NEED || it == Verdict.MASTERED }
    return (rational * 100.0 / known.size).toInt().coerceIn(0, 100)
}
